import { I18nPluralPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { CustomBaseStore } from '@city/const';
import { BaseStoreFacade, BaseStoreShopsFacade } from '@city/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PagesEntityStrapi } from '@seo/const';
import { PagesApiService, SeoActions } from '@seo/data-access';
import { mapDataToTemplateVariables } from '@seo/utils';
import { CustomPointOfService } from '@shop/const';
import { CmsActions, isNotNullable, PageMetaService, StringTemplate } from '@spartacus/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { SeoMetaService } from '../services';

@Injectable()
export class SeoEffects {
  loadPage$: Observable<SeoActions.PageLoadSuccess | SeoActions.PageLoadFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(SeoActions.LOAD_PAGE),
      map((action: SeoActions.PageLoad) => action.payload),
      switchMap(({ urlOrLabel }) => {
        // TODO: Сомнительное решение, не дергается экшн SeoActions.PageLoad и соотвественное нет состояния loading,
        // возможно, стоит сделать отдельный эффект на обновление города, и порешать вопрос нужно ли тогда в резолвере первый запрос
        // или разобраться, почему резолвер не дергается при смене города
        return this.baseStoreFacade.getActiveCode().pipe(map((baseStoreCode) => ({ urlOrLabel, baseStoreCode })));
      }),
      switchMap(({ urlOrLabel, baseStoreCode }) => {
        return this.pagesApiService.loadPage(urlOrLabel, baseStoreCode).pipe(
          switchMap((res) => (res ? this.resolveTemplateVariables(res) : of(null))),
          take(1),
          map((res) => new SeoActions.PageLoadSuccess(res)),
          catchError((error: unknown) => of(new SeoActions.PageLoadFail(error))),
        );
      }),
    ),
  );

  loadStrapiPageSuccess$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SeoActions.LOAD_PAGE_SUCCESS),
        map((action: SeoActions.PageLoadSuccess) => action.payload),
        filter(Boolean),
        map(({ seo }) => {
          this.seoMetaService.setMeta({
            canonicalUrl: seo.canonicalURL,
            description: seo.metaDescription,
            title: seo.metaTitle,
            robots: seo.metaRobots,
          });

          return void 0;
        }),
      ) as Observable<void>,
    { dispatch: false },
  );

  loadHybrisPageSuccess$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CmsActions.LOAD_CMS_PAGE_DATA_SUCCESS),
        switchMap(() =>
          this.pageMetaService.getMeta().pipe(
            filter(isNotNullable),
            // при переходе на страпиевскую страницу событие LOAD_CMS_PAGE_DATA_SUCCESS все равно эмитится,
            // поэтому отменяем подписку если видим что загрузилась страпи-страница
            takeUntil(
              this.actions$.pipe(
                ofType(SeoActions.LOAD_PAGE_SUCCESS),
                filter((action: SeoActions.PageLoadSuccess) => Boolean(action.payload)),
              ),
            ),
          ),
        ),
        tap((meta) => this.seoMetaService.setMeta(meta)),
        map(() => void 0),
      ) as Observable<void>,
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private pagesApiService: PagesApiService,
    private baseStoreFacade: BaseStoreFacade,
    private baseStoreShopsFacade: BaseStoreShopsFacade,
    private i18nPluralPipe: I18nPluralPipe,
    private seoMetaService: SeoMetaService,
    private pageMetaService: PageMetaService,
  ) {}

  private resolveTemplateVariables(apiResponse: PagesEntityStrapi): Observable<PagesEntityStrapi> {
    return combineLatest({
      baseStore: this.baseStoreFacade.getActiveBaseStore(),
      baseStoreShops: this.baseStoreShopsFacade.activeBaseStoreShops$,
    }).pipe(
      map(({ baseStore, baseStoreShops }) => {
        const { seo } = apiResponse;

        const baseStoreTemplateVariables = baseStore
          ? mapDataToTemplateVariables(baseStore as Record<keyof CustomBaseStore, unknown>, 'activeCity')
          : {};

        const shopCountsTemplateVariables = this.mapShopCountsTemplateVariables(baseStoreShops);

        const templateVariables = { ...baseStoreTemplateVariables, ...shopCountsTemplateVariables };

        const handledSeo = Object.fromEntries(
          Object.entries(seo).map(([key, value]) => {
            if (typeof value === 'string') {
              return [key, StringTemplate.resolve(value, templateVariables)];
            }

            return [key, value];
          }),
        ) as typeof seo;

        return { ...apiResponse, seo: handledSeo };
      }),
    );
  }

  private mapShopCountsTemplateVariables(
    baseStoreShops: CustomPointOfService[],
  ): Record<'activeCity.shopCountsString' | 'activeCity.shopCounts', string | number> {
    const shopCountPluralString = this.i18nPluralPipe.transform(baseStoreShops.length, {
      one: 'магазин',
      few: 'магазина',
      many: 'магазинов',
    });
    const shopData: Record<'shopCountsString' | 'shopCounts', string | number> = {
      shopCountsString: `${baseStoreShops.length} ${shopCountPluralString}`,
      shopCounts: baseStoreShops.length,
    };

    return mapDataToTemplateVariables(shopData, 'activeCity');
  }
}
